import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/portal-client";
import type { AppRole } from "@/types/database";

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: AppRole[];
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  

  const fetchRoles = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (data && Array.isArray(data)) {
      setRoles(data.map((r: { role: string }) => r.role as AppRole));
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let sessionInitialized = false;

    const applySession = (newSession: Session | null) => {
      if (!mounted) return;

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        setTimeout(() => {
          void fetchRoles(newSession.user.id);
        }, 0);
      } else {
        setRoles([]);
      }
    };

    // Set up listener BEFORE getSession (Supabase best practice)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      applySession(newSession);

      if (event === "SIGNED_IN" && newSession?.user) {
        const email = newSession.user.email;
        if (email) {
          setTimeout(() => {
            void supabase
              .from("user_invitations")
              .update({ status: "accepted", accepted_at: new Date().toISOString() })
              .ilike("email", email)
              .eq("status", "pending");
          }, 0);
        }
      }


      if (sessionInitialized && mounted) {
        setLoading(false);
      }
    });

    void supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;

      sessionInitialized = true;
      applySession(s);
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchRoles]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const hasRole = (role: AppRole) => roles.includes(role);

  const resetPassword = async (email: string) => {
    const { error } = await supabase.functions.invoke("request-password-reset", {
      body: {
        email,
        next: "/management/login",
      },
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, roles, signIn, signOut, hasRole, resetPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
